import {useCallback, useContext, useMemo} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {
  atomButtonColors,
  atomButtonDesigns,
  atomButtonShapes,
  atomButtonSizes
} from '@s-ui/react-atom-button'
import AtomCard from '@s-ui/react-atom-card'
import AtomImage from '@s-ui/react-atom-image'
import Context from '@s-ui/react-context'

const BASE_CLASS = 'mt-CardVehicle'
const DEFAULT_PHOTO = 'images/no-photo.png'

export default function CardVehicle({
  title,
  price,
  fuelTypeId,
  power,
  image,
  vehicleId,
  onCardClick,
  onDetailClick,
  onConfigureClick
}) {
  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')
  const {FUEL_TYPES} = config.get('vehicleSpecifications')
  const urlStatics = config.get('urlStatics')

  const defaultPhotoSrc = `${urlStatics}${DEFAULT_PHOTO}`
  const imageSrc = image || defaultPhotoSrc

  const handleDetailClick = e => {
    e.preventDefault()
    e.stopPropagation()
    onDetailClick?.(vehicleId)
  }

  const handleConfigureClick = e => {
    e.preventDefault()
    e.stopPropagation()
    onConfigureClick?.(vehicleId)
  }

  const isCardClickable = onCardClick && typeof onCardClick === 'function'

  const MemoCardMedia = useCallback(
    () => <AtomImage className={`${BASE_CLASS}-mediaImage`} alt={title} src={imageSrc} title={title} />,
    [imageSrc, title]
  )

  const fuelTypeName = FUEL_TYPES[fuelTypeId]?.name

  const MemoContent = useMemo(
    () => (
      <div className={`${BASE_CLASS}-content`}>
        <h3 className={`${BASE_CLASS}-title`}>{title}</h3>

        {price > 0 && (
          <>
            <span className={`${BASE_CLASS}-priceLabel`}>{i18n.t('COMMON.PRICE_FROM')}</span>
            <p className={`${BASE_CLASS}-price`}>{i18n.c(price)}</p>
          </>
        )}

        <div className={`${BASE_CLASS}-specs`}>
          {fuelTypeName && <span className={`${BASE_CLASS}-fuelType`}> {i18n.t(fuelTypeName)} </span>}
          {power && <span className={`${BASE_CLASS}-power`}>{power}</span>}
        </div>

        <div className={`${BASE_CLASS}-actions`}>
          <AtomButton
            className={`${BASE_CLASS}-action`}
            color={atomButtonColors.SECONDARY}
            design={atomButtonDesigns.OUTLINE}
            fullWidth
            onClick={handleDetailClick}
            shape={atomButtonShapes.CIRCULAR}
            size={atomButtonSizes.MEDIUM}
          >
            {i18n.t('COMMON.SEE_DETAILS')}
          </AtomButton>

          <AtomButton
            className={`${BASE_CLASS}-action`}
            color={atomButtonColors.PRIMARY}
            design={atomButtonDesigns.SOLID}
            fullWidth
            onClick={handleConfigureClick}
            shape={atomButtonShapes.CIRCULAR}
            size={atomButtonSizes.MEDIUM}
          >
            {i18n.t('COMMON.CONFIGURE')}
          </AtomButton>
        </div>
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [title, price, fuelTypeName, power, i18n]
  )

  return (
    <div className={BASE_CLASS}>
      <AtomCard
        content={() => MemoContent}
        media={MemoCardMedia}
        vertical={true}
        {...(isCardClickable ? {onClick: onCardClick} : {})}
      />
    </div>
  )
}

CardVehicle.displayName = 'CardVehicle'

CardVehicle.propTypes = {
  title: PropTypes.string.isRequired,
  price: PropTypes.number.isRequired,
  fuelTypeId: PropTypes.number.isRequired,
  power: PropTypes.number,
  image: PropTypes.string,
  vehicleId: PropTypes.number.isRequired,
  onCardClick: PropTypes.func,
  onDetailClick: PropTypes.func,
  onConfigureClick: PropTypes.func
}
